/**
 * A conferência no portal na tela (E172.F7.T03): lê ao montar e a pedido, e registra. O registro
 * aceito troca o painel pela resposta do servidor; o recusado mantém o rascunho e diz o motivo.
 */
import { useCallback, useEffect, useState } from 'react';
import type { AxiosInstance } from 'axios';

import {
  ConferenciaNoPortalError,
  lerConferenciaNoPortal,
  registrarConferenciaNoPortal,
  type ConferenciaNoPortalInData,
  type MotivoDaFalhaDoPortal,
  type PainelDaConferenciaData,
} from '@/lib/conferenciaNoPortal';

export type EstadoDoPainel =
  | { estado: 'carregando' }
  | { estado: 'erro'; motivo: MotivoDaFalhaDoPortal }
  | { estado: 'pronto'; dados: PainelDaConferenciaData };

export type EnvioDaConferencia =
  | { estado: 'ocioso' }
  | { estado: 'enviando' }
  | { estado: 'registrada' }
  | { estado: 'recusada'; motivo: MotivoDaFalhaDoPortal };

function motivo(err: unknown): MotivoDaFalhaDoPortal {
  return err instanceof ConferenciaNoPortalError ? err.motivo : 'indisponivel';
}

export function useConferenciaNoPortal(
  client: AxiosInstance,
  caseId: string,
): {
  painel: EstadoDoPainel;
  envio: EnvioDaConferencia;
  reler: () => void;
  registrar: (entrada: ConferenciaNoPortalInData, print: File | null) => Promise<boolean>;
} {
  const [painel, setPainel] = useState<EstadoDoPainel>({ estado: 'carregando' });
  const [envio, setEnvio] = useState<EnvioDaConferencia>({ estado: 'ocioso' });
  const [pedido, setPedido] = useState(0);
  const reler = useCallback(() => setPedido((n) => n + 1), []);

  useEffect(() => {
    let vivo = true;
    setPainel((atual) => (atual.estado === 'pronto' ? atual : { estado: 'carregando' }));
    void (async () => {
      try {
        const dados = await lerConferenciaNoPortal(client, caseId);
        if (vivo) setPainel({ estado: 'pronto', dados });
      } catch (err) {
        if (vivo) setPainel({ estado: 'erro', motivo: motivo(err) });
      }
    })();
    return () => {
      vivo = false;
    };
  }, [client, caseId, pedido]);

  const registrar = useCallback(
    async (entrada: ConferenciaNoPortalInData, print: File | null): Promise<boolean> => {
      setEnvio({ estado: 'enviando' });
      try {
        const dados = await registrarConferenciaNoPortal(client, caseId, entrada, print);
        setPainel({ estado: 'pronto', dados });
        setEnvio({ estado: 'registrada' });
        return true;
      } catch (err) {
        setEnvio({ estado: 'recusada', motivo: motivo(err) });
        return false;
      }
    },
    [client, caseId],
  );

  return { painel, envio, reler, registrar };
}
